import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import {
  useDeleteConnector,
  usePatchConnector,
} from "@/features/connectors/hooks/use-connectors";
import type { Connector } from "@/features/connectors/types";
import { Pause, Play, Trash } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "@/features/utils/i18n";

export const useConnectorsBulkActions = (): BulkAction<Connector>[] => {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();
  const isOwner = useHasRole("OWNER");
  const deleteConnector = useDeleteConnector();
  const patchConnector = usePatchConnector();

  return useMemo(() => {
    if (!isOwner || !clientId) return [];

    const setStatus = (status: "active" | "inactive") => (rows: Connector[]) =>
      runBulk(
        rows.filter((connector) => connector.status !== status),
        (connector) =>
          patchConnector.mutateAsync({
            clientId,
            connectorId: connector.id!,
            data: { status },
          }),
        {
          success: (count) =>
            status === "active"
              ? t("bulk.activated", { count })
              : t("bulk.deactivated", { count }),
          error: (count) => t("bulk.updateError", { count }),
        },
      );

    return [
      {
        key: "activate",
        label: t("actions.activate"),
        icon: <Play className="h-4 w-4" weight="fill" />,
        isVisible: (rows) => rows.some((row) => row.status !== "active"),
        onAction: async (rows) => {
          await setStatus("active")(rows);
        },
      },
      {
        key: "deactivate",
        label: t("actions.deactivate"),
        icon: <Pause className="h-4 w-4" weight="fill" />,
        isVisible: (rows) => rows.some((row) => row.status === "active"),
        onAction: async (rows) => {
          await setStatus("inactive")(rows);
        },
      },
      {
        key: "delete",
        label: t("actions.delete"),
        icon: <Trash className="h-4 w-4" weight="fill" />,
        variant: "destructive",
        confirm: (rows) => t("bulk.deleteConfirm", { count: rows.length }),
        onAction: async (rows) => {
          await runBulk(
            rows,
            (connector) =>
              deleteConnector.mutateAsync({
                clientId,
                connectorId: connector.id!,
              }),
            {
              success: (count) => t("bulk.deleted", { count }),
              error: (count) => t("bulk.deleteError", { count }),
            },
          );
        },
      },
    ];
  }, [isOwner, clientId, deleteConnector, patchConnector, t]);
};
